import { useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserPlus,
  faCircleNotch,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import * as AgenticV2API from "@/services/api/agentic-v2";
import type { TaskDetail } from "@/services/api/agentic-v2";
import FormInput from "@/components/ui/form-input";
import SelectDropdown, { type SelectOption } from "@/components/ui/select-dropdown";
import WorkerAvatar from "@/components/worker/WorkerAvatar";

interface AssignableUser {
  user_id: string;
  email: string;
  display_name: string;
}

const UNASSIGNED_VALUE = "__unassigned__";

const FILTER_DROPDOWN_BUTTON_CLASS =
  "!h-8 !min-h-8 !py-1 !border-0 !ring-0 !bg-slate-100 !text-slate-700 dark:!bg-slate-800/80 dark:!text-slate-200 !text-xs";

function stopListboxEvent(event: React.SyntheticEvent) {
  event.stopPropagation();
}

function DropdownOpenTracker({
  onOpen,
  onClose,
}: {
  onOpen: () => void;
  onClose: () => void;
}) {
  useEffect(() => {
    onOpen();
    return onClose;
  }, [onOpen, onClose]);
  return null;
}

export interface TaskHumanAssignProps {
  task: TaskDetail;
  onAssigned: (task: TaskDetail) => void;
  /** Icon-only trigger for compact surfaces (e.g. review card header). */
  variant?: "default" | "icon";
}

/** Inline assignee chip + searchable user picker (standard SelectDropdown UI). */
export function TaskHumanAssign({
  task,
  onAssigned,
  variant = "default",
}: TaskHumanAssignProps) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [users, setUsers] = useState<AssignableUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyEmail, setBusyEmail] = useState<string | null>(null);

  useEffect(() => {
    if (!dropdownOpen) return;
    let cancelled = false;
    setLoading(true);
    AgenticV2API.listAssignableUsers(query)
      .then((rows) => {
        if (!cancelled) setUsers(rows);
      })
      .catch(() => {
        if (!cancelled) setUsers([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dropdownOpen, query]);

  const handleAssign = useCallback(
    async (email: string) => {
      if (!email || email === UNASSIGNED_VALUE || email === task.assigned_human_email) return;
      setBusyEmail(email);
      try {
        const result = await AgenticV2API.assignTaskHuman(task._id, {
          user_email: email,
        });
        if (!result.ok) return;
        const updated = await AgenticV2API.getTask(task._id);
        onAssigned(updated);
        setQuery("");
      } finally {
        setBusyEmail(null);
      }
    },
    [task._id, task.assigned_human_email, onAssigned],
  );

  const sortedUsers = useMemo(() => {
    const teamEmails = new Set(
      (task.entity_team ?? [])
        .map((m) => (m.email ?? "").toLowerCase())
        .filter(Boolean),
    );
    if (teamEmails.size === 0) return users;
    return [...users].sort((a, b) => {
      const aTeam = teamEmails.has(a.email.toLowerCase()) ? 0 : 1;
      const bTeam = teamEmails.has(b.email.toLowerCase()) ? 0 : 1;
      return aTeam - bTeam;
    });
  }, [users, task.entity_team]);

  const options: SelectOption<string>[] = useMemo(() => {
    const rows = sortedUsers.map((user) => ({
      value: user.email,
      label: user.display_name,
    }));
    const assignedEmail = task.assigned_human_email;
    if (
      assignedEmail &&
      !rows.some((row) => row.value === assignedEmail)
    ) {
      rows.unshift({
        value: assignedEmail,
        label: task.assigned_human_name ?? assignedEmail,
      });
    }
    return rows;
  }, [sortedUsers, task.assigned_human_email, task.assigned_human_name]);

  const listValue = task.assigned_human_email ?? UNASSIGNED_VALUE;
  const label = task.assigned_human_name ?? "Assign";

  const handleOpen = useCallback(() => setDropdownOpen(true), []);
  const handleClose = useCallback(() => {
    setDropdownOpen(false);
    setQuery("");
  }, []);

  const handleChange = useCallback(
    (email: string) => {
      void handleAssign(email);
    },
    [handleAssign],
  );

  const iconButtonClass =
    "!h-6 !min-h-6 !w-6 !max-w-6 !p-0 !border-0 !ring-0 !bg-transparent !text-slate-500 hover:!bg-slate-100 hover:!text-slate-700 dark:!text-slate-400 dark:hover:!bg-slate-800/80 dark:hover:!text-slate-200 !rounded-md [&>span:last-child]:!hidden";

  const defaultButtonClass = clsx(
    FILTER_DROPDOWN_BUTTON_CLASS,
    "!h-auto !min-h-0 !min-w-[5.25rem] !rounded-md !px-2 !pr-7",
    task.assigned_human_name
      ? ""
      : "!border !border-dashed !border-slate-300 !bg-transparent !text-slate-500 dark:!border-slate-600 dark:!text-slate-400",
  );

  return (
    <SelectDropdown<string>
      inline
      usePortal
      keepOpenOnSelect={() => false}
      options={options}
      value={listValue}
      onChange={handleChange}
      className="shrink-0"
      buttonClassName={variant === "icon" ? iconButtonClass : defaultButtonClass}
      dropdownWidth="w-72"
      data-testid="task-assign-trigger"
      renderSelectedItem={() =>
        variant === "icon" ? (
          <FontAwesomeIcon icon={faUserPlus} className="text-xs" aria-hidden />
        ) : (
          <span className="inline-flex items-center gap-1.5">
            {task.assigned_human_name ? (
              <WorkerAvatar
                seed={task.assigned_human_name}
                size={18}
                className="shrink-0"
              />
            ) : (
              <FontAwesomeIcon icon={faUserPlus} className="text-[10px]" aria-hidden />
            )}
            <span>{label}</span>
          </span>
        )
      }
      renderListHeader={() => (
        <div
          className="sticky top-0 z-10 border-b border-slate-200 bg-white p-2 dark:border-slate-600/60 dark:bg-[#283346]"
          onMouseDown={stopListboxEvent}
          onClick={stopListboxEvent}
        >
          <FormInput
            type="search"
            placeholder="Search teammates…"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={stopListboxEvent}
            autoFocus
            canClear
            showClearButton
            onClear={() => setQuery("")}
            aria-label="Search teammates"
            data-testid="task-assign-search"
          />
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-2 text-xs text-slate-500 dark:text-slate-400">
              <FontAwesomeIcon icon={faCircleNotch} spin className="text-xs" />
              Loading…
            </div>
          ) : null}
          {!loading && query.trim() && options.length === 0 ? (
            <p className="mt-2 px-1 text-xs text-slate-500 dark:text-slate-400">
              No users found
            </p>
          ) : null}
        </div>
      )}
      renderListItem={(option, _selected, focus) => (
        <div
          className="flex items-center gap-2.5 px-3 py-2"
          data-testid={`task-assign-option-${option.value}`}
        >
          <WorkerAvatar seed={option.label} size={28} className="shrink-0" />
          <div className="min-w-0">
            <span
              className={clsx(
                "block text-sm font-medium truncate",
                focus ? "text-white" : "text-slate-900 dark:text-slate-100",
                busyEmail === option.value && "opacity-60",
              )}
            >
              {option.label}
            </span>
            <span
              className={clsx(
                "block text-xs truncate",
                focus ? "text-indigo-100" : "text-slate-500 dark:text-slate-400",
              )}
            >
              {option.value}
            </span>
          </div>
        </div>
      )}
      renderListFooter={() => (
        <DropdownOpenTracker onOpen={handleOpen} onClose={handleClose} />
      )}
    />
  );
}
